import { useState, useRef, useCallback, useEffect } from 'react';
import {
  apiCreateContact,
  apiUpdateContact,
  apiGetContactOperationStatus,
  apiInitiateGetContactDetails,
} from 'api/quote-marketplace';
import type {
  ContactCreateData,
  ActurisContactDetails,
  ContactOperationResponse,
} from 'api/quote-marketplace';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 60;

interface UseContactOperationReturn {
  isPending: boolean;
  error: string | null;
  createContact: (data: ContactCreateData) => Promise<string>;
  updateContact: (
    contactRef: string,
    data: Partial<ContactCreateData>,
  ) => Promise<ActurisContactDetails | null>;
  getContactDetails: (contactRef: string) => Promise<ActurisContactDetails>;
  reset: () => void;
}

export const useContactOperation = (): UseContactOperationReturn => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const pollUntilComplete = useCallback(
    async (operationId: number): Promise<ContactOperationResponse> => {
      let attempts = 0;

      while (attempts < MAX_POLL_ATTEMPTS) {
        if (!mountedRef.current) {
          throw new Error('Component unmounted');
        }

        const response = await apiGetContactOperationStatus(operationId);

        if (response.status === 'complete') {
          return response;
        }

        if (response.status === 'duplicates_found') {
          throw new Error('A contact with this name already exists');
        }

        if (response.status === 'error') {
          throw new Error(response.error_message || 'Operation failed');
        }

        attempts++;
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }

      throw new Error('Operation timed out');
    },
    [],
  );

  const createContact = useCallback(
    async (data: ContactCreateData): Promise<string> => {
      setIsPending(true);
      setError(null);

      try {
        const { operation_id } = await apiCreateContact(data);
        const response = await pollUntilComplete(operation_id);
        setIsPending(false);
        return response.contact_ref || '';
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to create contact';
        if (mountedRef.current) {
          setError(errorMessage);
          setIsPending(false);
        }
        throw err;
      }
    },
    [pollUntilComplete],
  );

  const updateContact = useCallback(
    async (
      contactRef: string,
      data: Partial<ContactCreateData>,
    ): Promise<ActurisContactDetails | null> => {
      setIsPending(true);
      setError(null);

      try {
        const { operation_id } = await apiUpdateContact(contactRef, data);
        const response = await pollUntilComplete(operation_id);
        setIsPending(false);
        return response.contact || null;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to update contact';
        if (mountedRef.current) {
          setError(errorMessage);
          setIsPending(false);
        }
        throw err;
      }
    },
    [pollUntilComplete],
  );

  const getContactDetails = useCallback(
    async (contactRef: string): Promise<ActurisContactDetails> => {
      setIsPending(true);
      setError(null);

      try {
        const { operation_id } = await apiInitiateGetContactDetails(contactRef);
        const response = await pollUntilComplete(operation_id);
        setIsPending(false);

        if (!response.contact) {
          throw new Error('Contact data not returned');
        }

        return response.contact;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch contact';
        if (mountedRef.current) {
          setError(errorMessage);
          setIsPending(false);
        }
        throw err;
      }
    },
    [pollUntilComplete],
  );

  const reset = useCallback(() => {
    setIsPending(false);
    setError(null);
  }, []);

  return {
    isPending,
    error,
    createContact,
    updateContact,
    getContactDetails,
    reset,
  };
};
